<!--

 -->

<template>
  <div>
    <div class="gsl-hrm-background-container">
      <div class="gsl-hrm-card-container">
        <oxd-text class="gsl-hrm-main-title" tag="h6">
          {{ $t('performance.edit_review') }}
        </oxd-text>
        <oxd-divider />
        <oxd-form ref="formRef" :loading="isLoading">
          <oxd-form-row>
            <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <employee-autocomplete
                  v-model="review.employee"
                  :rules="rules.employee"
                  required
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-row>
            <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <supervisor-autocomplete
                  v-model="review.supervisorReviewer"
                  :rules="rules.supervisorReviewer"
                  required
                  :subordinate="review.employee"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-row>
            <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <date-input
                  v-model="review.startDate"
                  :label="$t('performance.review_period_start_date')"
                  :rules="rules.startDate"
                  required
                />
                <oxd-text
                  v-if="previousReviewDatesExist"
                  class="model-title"
                  @click="onOpenEmployeeReviewPeriod"
                >
                  Show existing review periods
                </oxd-text>
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="review.endDate"
                  :label="$t('performance.review_period_end_date')"
                  :rules="rules.endDate"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="review.dueDate"
                  :label="$t('performance.due_date')"
                  :rules="rules.dueDate"
                  required
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-form-row>
            <oxd-grid
              :cols="3"
              class="gsl-hrm-full-width-grid"
              style="padding: 5px 0"
            >
              <oxd-grid-item v-if="review.employee?.id">
                <oxd-check-box
                  v-model="review.kpiProjectAllocation"
                  :option-label="$t('performance.project_role_defined_kpi')"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-check-box
                  v-model="enableKpiSelection"
                  :option-label="$t('performance.enable_kpi_selection')"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-row v-if="enableKpiSelection">
            <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <job-title-autocomplete
                  :model-value="selectedJobTitles"
                  :rules="rules.kpis"
                  @update:model-value="updateJobTitles"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-row
            v-if="enableKpiSelection && selectedJobTitles.length"
            class="kpi-list-container"
          >
            <div
              v-for="job in selectedJobTitles"
              :key="job.id"
              class="kpi-body"
            >
              <oxd-text class="kpi-job-title" @click="toggleAccordion(job.id)">
                <span>{{ job.label }}</span>
                <span v-if="selectedCount(job) > 0" class="kpi-count">
                  {{
                    $t('general.n_records_selected', {
                      count: selectedCount(job),
                    })
                  }}
                </span>
              </oxd-text>
              <transition name="accordion">
                <div v-show="isOpen(job.id)" class="kpi-content">
                  <div v-if="filteredKPIsByJob(job.label)?.length > 0">
                    <div
                      v-for="kpi in filteredKPIsByJob(job.label)"
                      :key="kpi.id"
                      class="kpi-item"
                    >
                      <oxd-check-box
                        :model-value="review.kpis?.includes(kpi.id)"
                        :option-label="kpi.title"
                        @update:model-value="
                          (checked) => onKpiChange(kpi.id, checked)
                        "
                      />
                    </div>
                  </div>
                  <oxd-text v-else class="">
                    {{ $t('general.no_records_found') }}
                  </oxd-text>
                </div>
              </transition>
            </div>
          </oxd-form-row>

          <oxd-divider />
          <oxd-form-actions>
            <required-text />
            <div class="gsl-hrm-button-row">
              <oxd-button
                display-type="ghost"
                :label="$t('general.cancel')"
                type="button"
                @click="onCancel"
              />
              <oxd-button
                display-type="ghost"
                :label="$t('general.save')"
                type="submit"
                @click="onSave(false)"
              />
              <oxd-button
                display-type="secondary"
                :label="$t('performance.activate')"
                type="button"
                @click="onSave(true)"
              />
            </div>
          </oxd-form-actions>
        </oxd-form>
      </div>
    </div>
    <employee-review-period-list-modal
      v-if="showEmployeeReviewPeriodModal"
      :items="employeeReviewPeriod"
      @close="onEmployeeReviewPeriodModalClose"
    ></employee-review-period-list-modal>
  </div>
</template>

<script>
import {navigate} from '@/core/util/helper/navigation';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import SupervisorAutoComplete from '@/orangehrmPerformancePlugin/components/SupervisorAutoComplete';
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  validSelection,
  validDateFormat,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
} from '@/core/util/validation/rules';
import useForm from '@/core/util/composable/useForm';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {ref} from 'vue';
import JobTitleAutoComplete from '@/orangehrmPerformancePlugin/components/JobTitleAutoComplete';
import {promiseDebounce} from '@ohrm/oxd';
import EmployeeReviewPeriodListModal from '@/orangehrmPerformancePlugin/components/EmployeeReviewPeriodListModal';

const reviewModel = {
  employee: null,
  supervisorReviewer: null,
  startDate: null,
  endDate: null,
  dueDate: null,
  jobTitleIds: null,
  kpiProjectAllocation: false,
  kpis: [],
};

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'supervisor-autocomplete': SupervisorAutoComplete,
    'job-title-autocomplete': JobTitleAutoComplete,
    'employee-review-period-list-modal': EmployeeReviewPeriodListModal,
  },
  props: {
    reviewId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const {formRef, invalid, validate} = useForm();
    const {userDateFormat} = useDateFormat();

    const selectedJobTitles = ref([]);
    const kpiOptions = ref([]);
    const openAccordionId = ref(null);
    const enableKpiSelection = ref(false);

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/performance/manage/reviews',
    );
    http.setIgnorePath('/api/v2/performance/manage/reviews/[0-9]+');

    const kpiHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/performance/kpis',
    );

    return {
      formRef,
      invalid,
      validate,
      http,
      userDateFormat,
      selectedJobTitles,

      kpiOptions,
      kpiHttp,

      enableKpiSelection,
      openAccordionId,
    };
  },
  data() {
    return {
      isLoading: false,
      showEmployeeReviewPeriodModal: false,
      employeeReviewPeriod: [],
      previousReviewDatesExist: false,
      review: {...reviewModel},
      rules: {
        employee: [required, validSelection],
        supervisorReviewer: [required, validSelection],
        startDate: [
          required,
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.review.endDate,
            this.$t(
              'performance.review_period_start_date_should_be_before_end_date',
            ),
          ),
          promiseDebounce(this.validateStartDate, 500),
        ],
        endDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.review.startDate,
            this.$t(
              'performance.review_period_end_date_should_be_after_start_date',
            ),
          ),
          promiseDebounce(this.validateEndDate, 500),
        ],
        dueDate: [
          required,
          validDateFormat(),
          endDateShouldBeAfterStartDate(
            () => this.review.endDate,
            this.$t(
              'performance.due_date_should_be_after_review_period_end_date',
            ),
          ),
        ],
        kpis: [
          (jobs) => {
            if (!this.enableKpiSelection) return true; // skip if not enabled
            if (!jobs?.length)
              return this.$t('performance.at_least_one_job_required');

            const missingJob = jobs.find((job) => {
              const jobKPIs = this.filteredKPIsByJob(job.label) || [];
              return !jobKPIs.some((kpi) => this.review.kpis?.includes(kpi.id));
            });

            if (missingJob) {
              return this.$t('performance.select_at_least_one_kpi_for_job');
            }

            return true;
          },
        ],
      },
    };
  },
  watch: {
    'review.employee'(employee) {
      this.previousReviewDatesExist = false;

      if (employee?.id) {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/performance/reviews/employee/review-dates`,
            params: {
              empNumber: employee?.id,
              recordId: this.reviewId,
            },
          })
          .then((res) => {
            const {data} = res.data;
            // const newDate = addDays(parseDate(data.fromDate), 1);
            // this.review.startDate = formatDate(newDate, 'yyyy-MM-dd');
            if (data?.reviewDate?.length > 0) {
              this.previousReviewDatesExist = true;
            }
            this.employeeReviewPeriod = data?.reviewDate;
          });
      }
    },
    'review.kpiProjectAllocation'(value) {
      if (value && this.review.employee?.id) {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/performance/kpis/project/allocation`,
            params: {
              empNumber: this.review.employee?.id,
            },
          })
          .then((res) => {
            const {data} = res.data;

            const jobMap = {};
            data.forEach((kpi) => {
              const job = kpi.jobTitle;
              if (!jobMap[job.id]) {
                jobMap[job.id] = {id: job.id, label: job.name};
              }
            });

            const existingJobMap = Array.isArray(this.selectedJobTitles)
              ? Object.fromEntries(this.selectedJobTitles.map((j) => [j.id, j]))
              : {};

            this.selectedJobTitles = Object.values({
              ...existingJobMap,
              ...jobMap,
            });

            const normalized = data?.map((item) => ({
              id: item.id,
              title: item.title,
              jobTitleName: item.jobTitle.name,
              jobTitleId: item.jobTitle.id,
            }));
            // Append without duplicates
            const existingIds = this.kpiOptions.map((kpi) => kpi.id);
            this.kpiOptions.push(
              ...normalized.filter((kpi) => !existingIds.includes(kpi.id)),
            );

            const preselectedIds = data.map((kpi) => kpi.id);
            const merged = new Set([...this.review.kpis, ...preselectedIds]);
            this.review.kpis = Array.from(merged);
          });
      }
    },
  },
  created() {
    this.isLoading = true;
    this.http
      .get(this.reviewId)
      .then((response) => {
        const {data} = response.data;
        this.review.employee = data.employee
          ? {
              id: data.employee.empNumber,
              label: `${data.employee.firstName} ${
                data.employee.middleName ? data.employee.middleName : ''
              } ${data.employee.lastName}`,
              isPastEmployee: data.employee.terminationId ? true : false,
            }
          : null;
        this.review.supervisorReviewer = data.reviewer.employee
          ? {
              id: data.reviewer.employee.empNumber,
              label: `${data.reviewer.employee.firstName} ${
                data.reviewer.employee.middleName
                  ? data.reviewer.employee.middleName
                  : ''
              } ${data.reviewer.employee.lastName}`,
              isPastEmployee: data.reviewer.employee.terminationId
                ? true
                : false,
            }
          : null;
        this.review.startDate = data.reviewPeriodStart;
        this.review.endDate = data.reviewPeriodEnd;
        this.review.dueDate = data.dueDate;
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    onEmployeeReviewPeriodModalClose() {
      this.showEmployeeReviewPeriodModal = false;
    },
    onOpenEmployeeReviewPeriod() {
      this.showEmployeeReviewPeriodModal = true;
    },
    onCancel() {
      navigate('/performance/searchPerformanceReview');
    },
    onKpiChange(kpiId, checked) {
      if (checked) {
        if (!this.review.kpis?.includes(kpiId)) {
          this.review.kpis?.push(kpiId);
        }
      } else {
        this.review.kpis = this.review.kpis?.filter((id) => id !== kpiId);
      }
    },
    selectedCount(job) {
      return this.kpiOptions.filter(
        (kpi) =>
          kpi.jobTitleId === job.id && this.review.kpis?.includes(kpi.id),
      ).length;
    },
    isOpen(jobId) {
      return this.openAccordionId === jobId;
    },
    toggleAccordion(jobId) {
      this.openAccordionId = this.openAccordionId === jobId ? null : jobId;
    },
    onSave(activate = false) {
      this.validate().then(() => {
        if (this.invalid === true) return;
        if (this.review.supervisorReviewer.isPastEmployee) {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: this.$t(
              'performance.cannot_add_a_past_employee_as_a_reviewer',
            ),
          });
        }
        this.isLoading = true;
        this.http
          .update(this.reviewId, {
            empNumber: this.review.employee.id,
            reviewerEmpNumber: this.review.supervisorReviewer.id,
            startDate: this.review.startDate,
            endDate: this.review.endDate,
            dueDate: this.review.dueDate,
            kpis: this.review.kpis,
            kpiProjectAllocation: this.review.kpiProjectAllocation,
            activate,
          })
          .then(() => {
            if (activate === true) {
              return this.$toast.success({
                title: this.$t('general.success'),
                message: this.$t('general.successfully_activated'),
              });
            } else {
              return this.$toast.updateSuccess();
            }
          })
          .then(() => {
            this.onCancel();
          })
          .catch((response) => {
            return this.$toast.warn({
              title: this.$t('general.warning'),
              message: response?.data.error.message,
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    filteredKPIsByJob(name) {
      return this.kpiOptions.filter((kpi) => kpi.jobTitleName === name);
    },
    validateStartDate() {
      if (!this.review.startDate || !this.review.employee) {
        return true;
      }

      return new Promise((resolve) => {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/performance/reviews/start-date-overlap`,
            params: {
              recordId: this.reviewId,
              startDate: this.review.startDate,
              empNumber: this.review.employee?.id,
            },
          })
          .then((res) => {
            const {data} = res.data;
            return data.valid === true
              ? resolve(true)
              : resolve(this.$t('attendance.overlapping_records_found'));
          });
      });
    },
    validateEndDate() {
      if (
        !this.review.startDate ||
        !this.review.endDate ||
        !this.review.employee
      ) {
        return true;
      }
      return new Promise((resolve) => {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/performance/reviews/end-date-overlap`,
            params: {
              recordId: this.reviewId,
              startDate: this.review.startDate,
              endDate: this.review.endDate,
              empNumber: this.review.employee?.id,
            },
          })
          .then((res) => {
            const {data} = res.data;
            return data.valid === true
              ? resolve(true)
              : resolve(this.$t('attendance.overlapping_records_found'));
          });
      });
    },
    async updateJobTitles(jobTitles) {
      const oldJobIds = this.selectedJobTitles?.map((j) => j.id);
      const newJobIds = jobTitles.map((j) => j.id);

      this.selectedJobTitles = jobTitles;

      if (jobTitles.length > 0) {
        this.openAccordionId = jobTitles[0]?.id;
      } else {
        this.openAccordionId = null;
      }

      // Find removed job titles
      const removedJobIds = oldJobIds.filter((id) => !newJobIds.includes(id));
      if (removedJobIds.length) {
        // Remove KPIs of removed jobs
        this.kpiOptions = this.kpiOptions.filter(
          (kpi) => !removedJobIds.includes(kpi.jobTitleId),
        );
        // Remove selected KPIs of removed jobs
        this.review.kpis = this.review.kpis.filter((id) =>
          this.kpiOptions.some((kpi) => kpi.id === id),
        );
      }

      // Fetch KPIs for newly added job titles
      const addedJobs = jobTitles.filter((j) => !oldJobIds.includes(j.id));
      for (const job of addedJobs) {
        const {data} = await this.kpiHttp.getAll({
          jobTitleId: job.id,
          mandatoryKpi: false,
        });
        const normalized = data?.data.map((item) => ({
          id: item.id,
          title: item.title,
          jobTitleName: item.jobTitle.name,
          jobTitleId: item.jobTitle.id,
        }));
        // Append without duplicates
        const existingIds = this.kpiOptions.map((kpi) => kpi.id);
        this.kpiOptions.push(
          ...normalized.filter((kpi) => !existingIds.includes(kpi.id)),
        );
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.model-title {
  font-size: 0.85rem;
  color: $oxd-primary-one-color;
  cursor: pointer;
  margin-bottom: 10px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
.kpi-list-container {
  margin-bottom: 2px;
  border: 1px solid #ddd;
  border-radius: 10px;
  overflow: hidden;
  font-family: $oxd-font-family;
}

.kpi-job-title {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 0.85rem;
  background-color: #f0f0f0;
  padding: 8px 10px;
  cursor: pointer;
}
.kpi-count {
  font-weight: normal;
  color: $oxd-primary-one-color;
}
.kpi-body:not(:last-child) .kpi-job-title {
  border-bottom: 1px solid #ddd;
}
.kpi-content {
  padding: 10px 10px;
  font-size: 0.8rem;
}
.kpi-item {
  gap: 10px;
  padding: 4px 0px;
}
</style>
<style src="./review.scss" lang="scss" scoped></style>
